import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { completeStaffEmailVerification, requestStaffEmailVerification } from "../api/auth";
import { useAuth } from "../context/AuthContext";
import { getEllyannaAccessGate } from "../utils/ellyannaAccessGate";
import { getTechnicianLandingPath } from "../utils/technicianRouting";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, refreshTechnicianOnboarding } = useAuth();

  const token = (searchParams.get("token") || "").trim();
  const initialEmail =
    (searchParams.get("email") || location.state?.email || "").trim();
  const salonId = (searchParams.get("salonId") || "").trim();

  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(Boolean(token));
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState(
    token
      ? "Verifying your email..."
      : "Check your inbox for the verification link, or resend it below."
  );

  useEffect(() => {
    let active = true;
    if (!token) return undefined;

    const verify = async () => {
      try {
        const data = await completeStaffEmailVerification(token);
        if (!active) return;

        if (data?.access_token) {
          let onboardingData = null;
          const normalizedRole = (data.role || "").toLowerCase();
          if (normalizedRole === "technician") {
            onboardingData = await refreshTechnicianOnboarding({
              accessToken: data.access_token,
            });
          }

          login(data.access_token, {
            ...data,
            salons:
              onboardingData?.accessible_salons ??
              data.salons ??
              data.accessibleSalons ??
              data.accessible_salons ??
              [],
            accessibleSalons:
              onboardingData?.accessible_salons ??
              data.salons ??
              data.accessibleSalons ??
              data.accessible_salons ??
              [],
            technician_status: onboardingData
              ? {
                  profile_complete: onboardingData.profile_complete ?? false,
                  staff_setup_complete: onboardingData.staff_setup_complete ?? null,
                  has_selected_salon:
                    onboardingData.has_selected_salon ?? Boolean(data.salon_id),
                  salon_id: onboardingData.salon_id ?? data.salon_id ?? null,
                  setup_complete: Boolean(
                    onboardingData.staff_setup_complete ?? false
                  ),
                }
              : undefined,
          });

          if (data.must_change_password) {
            navigate("/change-password", { replace: true });
            return;
          }

          if (normalizedRole === "technician") {
            navigate(
              getTechnicianLandingPath({
                hasSelectedSalon:
                  onboardingData?.has_selected_salon ?? Boolean(data.salon_id),
                staffSetupComplete:
                  onboardingData?.staff_setup_complete ?? false,
                profileComplete: onboardingData?.profile_complete ?? false,
              }),
              { replace: true }
            );
            return;
          }

          if (normalizedRole === "independent_professional") {
            const accessGate = getEllyannaAccessGate(normalizedRole, data);
            navigate(accessGate?.destination || "/manager/schedule", {
              replace: true,
            });
            return;
          }

          const accessGate = getEllyannaAccessGate(normalizedRole, data);
          navigate(accessGate?.destination || "/manager/schedule", {
            replace: true,
          });
          return;
        }

        setMessage(
          data?.message ||
            "Email verified. Sign in after a salon owner or manager approves your request."
        );
      } catch (err) {
        if (!active) return;
        setError(err.message || "Unable to verify your email.");
        setMessage("");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void verify();
    return () => {
      active = false;
    };
  }, [login, navigate, refreshTechnicianOnboarding, token]);

  const handleResend = async (event) => {
    event.preventDefault();
    setError("");
    setResending(true);
    try {
      const result = await requestStaffEmailVerification(email.trim());
      setMessage(
        result?.message || "If an account matches, we emailed a verification link."
      );
    } catch (err) {
      setError(err.message || "Unable to resend the verification email.");
    } finally {
      setResending(false);
    }
  };

  const loginTarget = salonId
    ? `/login?salonId=${encodeURIComponent(salonId)}`
    : "/login";

  return (
    <div className="min-h-screen bg-[#fdeee9] px-4 py-8">
      <div className="mx-auto max-w-3xl rounded-[28px] border border-white/70 bg-white/90 p-6 shadow-[0_20px_70px_rgba(47,53,102,0.12)] backdrop-blur sm:p-8">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-600">
          Ellyanna verification
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl">
          Verify your email
        </h1>
        <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
          Verify your email once to activate your Ellyanna sign-in. After that, you
          can continue setup or wait for salon approval, depending on your registration
          path.
        </p>

        {message ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            {message}
          </div>
        ) : null}
        {error ? (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        {!token ? (
          <form onSubmit={handleResend} className="mt-6 space-y-4">
            <div>
              <label
                htmlFor="ops-verify-email"
                className="block text-xs font-medium uppercase tracking-wide text-slate-700"
              >
                Email
              </label>
              <input
                id="ops-verify-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-200"
                placeholder="you@example.com"
                required
              />
            </div>
            <button
              type="submit"
              disabled={resending}
              className="w-full rounded-xl bg-rose-500 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {resending ? "Sending..." : "Resend verification email"}
            </button>
          </form>
        ) : null}

        <div className="mt-6 flex flex-wrap gap-3 text-sm">
          <Link
            to={loginTarget}
            className="inline-flex items-center justify-center rounded-full border border-[#2f3566]/20 bg-white px-4 py-2 font-semibold text-[#2f3566] transition hover:bg-[#2f3566]/5"
          >
            Back to login
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-slate-50 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            Back to registration
          </Link>
        </div>

        {loading ? (
          <div className="mt-6 text-sm text-slate-500">Finishing verification...</div>
        ) : null}
      </div>
    </div>
  );
}
